/**
 * Create a note in a workspace. In an end-to-end encrypted project the note is built and
 * encrypted here (title, initial text and first version) and the server only stores ciphertext.
 */
import * as Y from "yjs";
import { ENCRYPTED_TITLE_PLACEHOLDER } from "@brainlog/shared";
import { get, post } from "@/lib/api";
import { encSnapshot, encTitle, encVersion, requireKey } from "./ops";

export async function createNoteIn(
  ws: { id: string; encryptionScopeId?: string | null },
  opts: { folderId?: string | null; title?: string; templateKey?: string | null; kind?: string },
): Promise<{ id: string; title: string; encrypted: boolean }> {
  if (!ws.encryptionScopeId) {
    const { note } = await post<{ note: { id: string; title: string } }>("/api/notes", {
      workspaceId: ws.id,
      folderId: opts.folderId ?? null,
      title: opts.title?.trim() || undefined,
      templateKey: opts.templateKey || null,
      ...(opts.kind ? { kind: opts.kind } : {}),
    });
    return { ...note, encrypted: false };
  }
  const scopeId = ws.encryptionScopeId;
  requireKey(scopeId);
  let body = "";
  let kind = opts.kind ?? "note";
  let title = opts.title?.trim() ?? "";
  if (opts.templateKey) {
    const { templates } = await get<{ templates: { key: string; name: string; kind: string; bodyMd: string }[] }>(`/api/workspaces/${ws.id}/note-templates`);
    const t = templates.find((x) => x.key === opts.templateKey);
    if (t) {
      body = t.bodyMd;
      if (kind === "note") kind = t.kind;
      if (!title) title = t.name;
    }
  }
  title = title || "Untitled";
  const id = crypto.randomUUID();
  const versionId = crypto.randomUUID();
  const doc = new Y.Doc();
  doc.getText("content").insert(0, body);
  const state = Y.encodeStateAsUpdate(doc);
  doc.destroy();
  await post("/api/notes/encrypted", {
    id,
    workspaceId: ws.id,
    folderId: opts.folderId ?? null,
    kind,
    scopeId,
    keyProof: requireKey(scopeId).proof,
    encTitle: await encTitle(scopeId, id, title),
    snapshot: await encSnapshot(scopeId, id, state),
    version: { id: versionId, blob: await encVersion(scopeId, id, versionId, { title, text: body }) },
  });
  // Tabs are persisted, so they get the placeholder; the real title is shown from memory.
  return { id, title: ENCRYPTED_TITLE_PLACEHOLDER, encrypted: true };
}
